/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { useEffect } from 'react'
import { useIntl } from 'react-intl'

import { EuiFormRow, EuiKeyPadMenu, EuiKeyPadMenuItem } from '@elastic/eui'

import CloudProvider from '@modules/cloud-components/CloudProvider'
import type { CSP } from '@modules/ui-types/providers'

type Props = {
  value: CSP | ''
  onChange: (value: CSP) => void
  isDisabled: boolean
}

type CloudProviderOption = {
  value: CSP
  label: JSX.Element
}

const OPTIONS: CloudProviderOption[] = [
  {
    value: 'aws',
    label: <CloudProvider id='aws' text='short' showIcon={false} />,
  },
  {
    value: 'gcp',
    label: <CloudProvider id='gcp' text='short' showIcon={false} />,
  },
  {
    value: 'azure',
    label: <CloudProvider id='azure' text='short' showIcon={false} />,
  },
]

const CloudProviderField: React.FunctionComponent<Props> = ({ value, onChange, isDisabled }) => {
  const { formatMessage } = useIntl()

  // Set the provider if we don't have one yet
  useEffect(() => {
    if (value) {
      return
    }

    const [firstOption] = OPTIONS

    if (firstOption) {
      onChange(firstOption.value)
    }
  }, [value, onChange])

  return (
    <EuiFormRow
      label={formatMessage({
        id: 'networkSecurity.trafficFilters.rulesets.label.cloudProvider',
        defaultMessage: 'Select cloud provider',
      })}
      fullWidth={true}
    >
      <EuiKeyPadMenu checkable={true}>
        {OPTIONS.map((option) => (
          <EuiKeyPadMenuItem
            key={option.value}
            label={option.label}
            name='cloudProvider'
            checkable='single'
            onChange={() => onChange(option.value)}
            isSelected={value === option.value}
            isDisabled={isDisabled}
          >
            <CloudProvider id={option.value} text='none' showIcon={true} />
          </EuiKeyPadMenuItem>
        ))}
      </EuiKeyPadMenu>
    </EuiFormRow>
  )
}

export default CloudProviderField
